/** @jsxImportSource @emotion/react */
import {
  createStyles,
  Icon,
  Input,
  Select,
  Typography,
  useTheme,
  Modal,
  ListItem,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useMemo, useReducer, useState } from 'react';
import { useAsyncFn } from 'react-use';
import { Header } from '../../../../components';
import { useSession } from '../../../../context/Session';
import { isNewUser, NewUser, Role, User } from '../../../../types';
import { AuthenticatorEnabledStatus } from '../../../components/AuthenticatorEnabledStatus';
import { PermissionList } from '../../../components/PermissionList';
import { validateEmail } from '../../../utils/inputValidation';
import { deleteUser, saveUserPermissions, createUser } from '../../api';

interface Props {
  user: User | 'new';
  roles: Role[];
  permissions: string[];
  cancel: () => void;
}

interface UpdateUserAction {
  action: string;
  payload: string | string[];
}

export const UserDetails = ({
  user: propUser,
  roles,
  permissions,
  cancel,
}: Props) => {
  const styles = useStyles();
  const { palette, spacing } = useTheme();
  const session = useSession();

  const canEditUser =
    session.authenticated && session.permissions.includes('edit_users');
  const canEditPermissions =
    session.authenticated && session.permissions.includes('edit_permissions');

  const defaultUser: User | NewUser = useMemo(
    () =>
      propUser === 'new'
        ? {
            userId: 'new',
            email: '',
            permissions: [],
            groups: [],
            authenticatorEnabled: false,
          }
        : propUser,
    [propUser]
  );

  const [user, updateUser] = useReducer(
    (currentUser: User | NewUser, { action, payload }: UpdateUserAction) => {
      return {
        ...currentUser,
        [action]: payload,
      } as User | NewUser;
    },
    defaultUser
  );

  const [{ loading: saveLoading }, save] = useAsyncFn(async () => {
    if (!canEditPermissions) return;
    return saveUserPermissions(user);
  }, [user, canEditPermissions]);

  const [{ loading: deleteLoading }, remove] = useAsyncFn(async () => {
    if (!canEditUser) return;
    await deleteUser(user.userId);
    cancel();
  }, [user, canEditUser]);

  const [{ loading: createLoading }, create] = useAsyncFn(async () => {
    if (!isNewUser(user)) {
      throw Error('Creating existing user');
    }
    if (!canEditUser) return;
    await createUser(user);
    cancel();
  }, [user, canEditUser]);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const createUserValid = isNewUser(user) && validateEmail(user.email);

  const permissionsViaUserRoles = useMemo(() => {
    const result: Record<string, Set<string>> = {};

    roles
      .filter((r) => user.groups.includes(r.name))
      .forEach((role) => {
        role.permissions.forEach((permission) => {
          if (!(permission in result)) {
            result[permission] = new Set([role.name]);
          } else {
            const prev = Array.from(result[permission]);
            result[permission] = new Set([...prev, role.name]);
          }
        });
      });

    return result;
  }, [roles, user]);

  return (
    <div>
      <Header>
        <div css={styles.headerContent}>
          <div css={styles.titleContainer}>
            <div css={styles.back} onClick={cancel}>
              <Typography variant="subtitle" color={palette.grey.x300}>
                Users
              </Typography>
            </div>
            <div css={styles.chevron}>
              <Icon name="chevron" size={spacing.xxs} />
            </div>
            <Typography variant="subtitle">
              {isNewUser(user) ? 'New user' : user.email}
            </Typography>
          </div>
          <UtilityButton text="Cancel" variant="secondary" onClick={cancel} />
          {isNewUser(user) && canEditPermissions && (
            <UtilityButton
              text="Create user"
              variant="primary"
              loading={createLoading}
              disabled={!createUserValid}
              onClick={create}
            />
          )}
          {!isNewUser(user) && canEditPermissions && (
            <UtilityButton
              text="Save user"
              variant="primary"
              loading={saveLoading}
              onClick={save}
            />
          )}
        </div>
      </Header>
      <div css={styles.wrappingContainer}>
        <div css={[styles.container, styles.inputWrapper]}>
          <Input
            label="User ID"
            value={user.userId}
            onChange={() => ({})}
            disabled={true}
          />
          <Input
            label="Email"
            value={user.email}
            onChange={(e) =>
              isNewUser(user) &&
              updateUser({ action: 'email', payload: e.target.value })
            }
            error={user.email.length !== 0 && !validateEmail(user.email)}
            helperText={
              user.email.length !== 0 && !validateEmail(user.email)
                ? 'Please enter a valid email'
                : ''
            }
            disabled={!isNewUser(user)}
          />
          {user.createdBy && (
            <Input
              label="Created by"
              value={user.createdBy}
              onChange={() => ({})}
              disabled={true}
            />
          )}
          <Select
            label="Roles"
            multiple
            options={roles.map((r) => ({ label: r.name, value: r.name }))}
            onSelect={(selection) => {
              updateUser({ action: 'groups', payload: selection });
            }}
            disabled={!canEditPermissions}
            values={user.groups}
          />
          <AuthenticatorEnabledStatus
            enabled={user.authenticatorEnabled}
            subtitle={
              user.authenticatorEnabled
                ? 'This user has a connected MFA authenticator'
                : 'This user has not added an MFA authenticator'
            }
          />
          {!isNewUser(user) && canEditUser && (
            <div css={deleteLoading && styles.removeLoading}>
              <ListItem
                title="Remove user"
                icon="trash"
                onClick={() => {
                  setDeleteModalOpen(true);
                }}
                disabled={deleteLoading}
                action={deleteLoading && <Icon name="spinner" />}
              />
            </div>
          )}
        </div>
        <div css={styles.container}>
          <PermissionList
            selectedInstancePermissions={user.permissions}
            permissions={permissions.map((p) => ({
              name: p,
              subtitle:
                p in permissionsViaUserRoles
                  ? Array.from(permissionsViaUserRoles[p]).join(', ')
                  : undefined,
              checked:
                user.permissions.includes(p) || p in permissionsViaUserRoles,
              disabled: p in permissionsViaUserRoles,
            }))}
            updatePermissions={
              canEditPermissions
                ? (newPermissions) =>
                    updateUser({
                      action: 'permissions',
                      payload: newPermissions,
                    })
                : undefined
            }
          />
        </div>
      </div>
      {deleteModalOpen && (
        <Modal
          title="Are you sure?"
          subtitle="Removing a user can not be undone."
          secondaryButtonProps={{
            text: 'Cancel',
            onClick: () => setDeleteModalOpen(false),
          }}
          primaryButtonProps={{
            text: 'Confirm',
            onClick: () => remove(),
          }}
          closeModal={() => setDeleteModalOpen(false)}
        />
      )}
    </div>
  );
};

const useStyles = createStyles(({ spacing, shape, palette }) => ({
  wrappingContainer: {
    width: '100%',
    display: 'flex',
    gap: spacing.xs,
  },
  container: {
    flex: 1,
    background: palette.white,
    borderRadius: shape.borderRadius,
  },
  inputWrapper: {
    marginBottom: 'auto',
    padding: spacing.xxs,
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: spacing.xxs,
    border: `1px solid ${palette.grey.x100}`,
    '& li': {
      borderRadius: spacing.xxxs,
      border: `1px solid ${palette.grey.x200}`,
      ':hover': {
        borderColor: palette.grey.x600,
      },
    },
  },
  headerContent: {
    display: 'grid',
    gap: spacing.xxs,
    gridTemplateColumns: '1fr auto auto',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  back: {
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
      textDecorationColor: palette.grey.x300,
    },
  },
  chevron: {
    transform: 'rotate(180deg)',
    padding: `0 ${spacing.xxs}`,
    svg: { fill: 'none', stroke: palette.grey.x300 },
  },
  removeLoading: { pointerEvents: 'none' },
}));
